import React, { useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { Button } from './ui/button.jsx';
import { Input } from './ui/input.jsx';
import { Label } from './ui/label.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select.jsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.jsx';

const AddCategoryDialog = ({ isOpen, onClose, onSave }) => {
  const [categoryName, setCategoryName] = useState('');
  const [fields, setFields] = useState([
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'room', label: 'Room', type: 'text', required: false },
    { name: 'address', label: 'Address', type: 'text', required: false }
  ]);

  const fieldTypes = [
    { value: 'text', label: 'Text Input' },
    { value: 'select', label: 'Dropdown Select' },
    { value: 'number', label: 'Number' }
  ];

  const addField = () => {
    setFields([...fields, {
      name: '',
      label: '',
      type: 'text',
      required: false,
      options: []
    }]);
  };

  const updateField = (index, key, value) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], [key]: value };
    
    // Auto-generate name from label
    if (key === 'label' && !newFields[index].name) {
      newFields[index].name = value.toLowerCase().replace(/[^a-z0-9]/g, '_');
    }
    
    setFields(newFields);
  };

  const removeField = (index) => {
    if (fields.length > 1) {
      setFields(fields.filter((_, i) => i !== index));
    }
  };

  const handleSave = () => {
    if (!categoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    if (fields.some(field => !field.name || !field.label)) {
      alert('Please fill in all field names and labels');
      return;
    }

    onSave(categoryName, fields);
    
    // Reset form
    setCategoryName('');
    setFields([
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'room', label: 'Room', type: 'text', required: false },
      { name: 'address', label: 'Address', type: 'text', required: false }
    ]);
  };

  const handleClose = () => {
    setCategoryName('');
    setFields([
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'room', label: 'Room', type: 'text', required: false },
      { name: 'address', label: 'Address', type: 'text', required: false }
    ]);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Custom Device Category</DialogTitle>
          <DialogDescription>
            Create a new device category with custom fields for your specific equipment needs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Category Name */}
          <div>
            <Label htmlFor="category-name">Category Name *</Label>
            <Input
              id="category-name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g., Pool Equipment, Irrigation, Motors"
              data-testid="category-name-input"
            />
          </div>

          {/* Fields Configuration */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <Label>Device Fields</Label>
              <Button
                onClick={addField}
                variant="outline"
                size="sm"
                data-testid="add-field-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Field
              </Button>
            </div>

            <div className="space-y-3">
              {fields.map((field, index) => (
                <div key={index} className="border rounded-lg p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Field {index + 1}</span>
                    {fields.length > 1 && (
                      <Button
                        onClick={() => removeField(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        data-testid={`remove-field-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <Label htmlFor={`field-label-${index}`}>Label *</Label>
                      <Input
                        id={`field-label-${index}`}
                        value={field.label}
                        onChange={(e) => updateField(index, 'label', e.target.value)}
                        placeholder="e.g., Pump Type"
                        data-testid={`field-label-${index}`}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`field-name-${index}`}>Field Name</Label>
                      <Input
                        id={`field-name-${index}`}
                        value={field.name}
                        onChange={(e) => updateField(index, 'name', e.target.value)}
                        placeholder="auto-generated"
                        data-testid={`field-name-${index}`}
                      />
                    </div>

                    <div>
                      <Label htmlFor={`field-type-${index}`}>Type</Label>
                      <Select value={field.type} onValueChange={(value) => updateField(index, 'type', value)}>
                        <SelectTrigger data-testid={`field-type-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {fieldTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {field.type === 'select' && (
                    <div>
                      <Label htmlFor={`field-options-${index}`}>Options (comma-separated)</Label>
                      <Input
                        id={`field-options-${index}`}
                        value={field.options?.join(', ') || ''}
                        onChange={(e) => updateField(index, 'options', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                        placeholder="e.g., Pool Pump, Spa Pump, Filter Pump"
                        data-testid={`field-options-${index}`}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="border rounded-lg p-3 bg-gray-50">
            <Label className="text-sm font-medium">Preview:</Label>
            <div className="mt-2">
              <div className="font-medium">{categoryName || 'New Category'}</div>
              <div className="text-sm text-gray-600 mt-1">
                Fields: {fields.map(f => f.label).filter(l => l).join(', ') || 'None defined'}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} data-testid="save-category-btn">
            Create Category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddCategoryDialog;